import { createI18n } from '@wiztime/i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/tokens.css';
import './styles/global.css';
import { App } from './app/App';
import { loadLocale } from './app/locale';
import { requestPersistence } from './app/persistence';
import { MigrationStatus } from '@wiztime/storage';
import { createServices, migrateStoredData } from './app/services';
import { MigrationBlocked } from './app/MigrationBlocked';
import { installTestHooks } from './app/testHooks';
import { trackPickerDirty } from './app/pickerReloadBlocker';
import { trackUnsavedEdits } from './app/unsavedEdits';
import {
  combineGestureTrackers,
  reloadWhenWorkerUpdates,
  scheduleWorkerUpdateChecks,
  trackPointerGestures,
} from './app/workerUpdate';
import { readStorage, writeStorage } from './lib/safeStorage';
import { FLAG_ON, StorageKey } from './lib/storageKeys';
import { toastNotice } from './shell/toastStore';
import { setLocalSettingLogger } from './lib/localSettingLog';
import { OpLogKind } from '@wiztime/core';

const workerContainer = navigator.serviceWorker as ServiceWorkerContainer | undefined;
reloadWhenWorkerUpdates(
  workerContainer,
  () => {
    window.location.reload();
  },
  // The reload waits for pointer gestures, unsaved picker fields (2026-10b §5.4) and other unsaved edits.
  combineGestureTrackers(trackPointerGestures(window, document), trackPickerDirty(), trackUnsavedEdits()),
);
scheduleWorkerUpdateChecks(
  workerContainer === undefined ? undefined : () => workerContainer.getRegistration(),
  document,
  (handler, intervalMs) => window.setInterval(handler, intervalMs),
);

const locale = loadLocale();
// Only the default language is in the first-load script; another one is fetched here before the first render.
const i18n = await createI18n(locale);
document.documentElement.lang = locale;
document.title = i18n.t('app.name');

const services = createServices();
// Nothing may write before the stored data is in this build's format (data dictionary §10).
const migration = await migrateStoredData(services.store).catch((error: unknown) => ({
  status: MigrationStatus.Failed,
  version: 0,
  ran: [] as number[],
  error: error instanceof Error ? error.message : String(error),
}));
installTestHooks(services);
const blocked = migration.status !== MigrationStatus.Ready;

if (!blocked) {
  void services.sync.start();
  setLocalSettingLogger((name, value) => {
    void services.store.logOperation({ kind: OpLogKind.LocalSetting, name, summary: { value } }).catch((error: unknown) => {
      console.warn('[oplog] local setting entry failed', error);
    });
  });
}

void requestPersistence().then((persistent) => {
  // Once dismissed, the notice stays away: the export reminder covers the same risk.
  if (persistent || readStorage(StorageKey.PersistNoticeDismissed) === FLAG_ON) return;
  toastNotice(i18n.t('persist.denied'), () => writeStorage(StorageKey.PersistNoticeDismissed, FLAG_ON));
});

const root = document.getElementById('root');
if (root === null) throw new Error('Missing #root element');
createRoot(root).render(
  <StrictMode>
    {blocked ? (
      <MigrationBlocked store={services.store} outcome={migration} i18n={i18n} />
    ) : (
      <App services={services} i18n={i18n} />
    )}
  </StrictMode>,
);
